import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader, Button } from 'components/ui';
import { LoadingState, ErrorState } from 'components/feedback';
import { useToast } from 'components/toast/ToastProvider';
import { ROUTES } from 'lib/constants';
import { useCatalogItem } from '../hooks/useCatalogItem';
import { useUpdateCatalogItem } from '../hooks/useUpdateCatalogItem';
import { ProductForm } from '../components/ProductForm';
import { catalogItemToFormValues } from '../utils/buildCatalogInput';

/** Edit an existing catalog item: prefill from the API, save via update mutation. */
export function EditProductPage() {
  const { catalogId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const { item, loading, error, refetch } = useCatalogItem(catalogId);
  const { updateCatalogItem, loading: saving } = useUpdateCatalogItem(catalogId);

  const handleSubmit = async (input) => {
    try {
      const payload = await updateCatalogItem(input);
      if (!payload?.success) {
        toast.error(
          'Could not save changes',
          payload?.errors?.[0]?.message ||
            payload?.message ||
            'Please review the form and try again.',
        );
      }
      return payload;
    } catch (err) {
      toast.error('Something went wrong', err.message);
      return null;
    }
  };

  const handleSuccess = (payload) => {
    toast.success('Changes saved', payload.message || 'Your product was updated.');
    navigate(ROUTES.CATALOG);
  };

  let body;
  if (loading && !item) {
    body = <LoadingState label="Loading product…" />;
  } else if (error && !item) {
    body = <ErrorState error={error} onRetry={() => refetch()} />;
  } else if (!item) {
    body = (
      <ErrorState
        title="Product not found"
        description="This catalog item could not be loaded."
      />
    );
  } else {
    body = (
      <ProductForm
        initialValues={catalogItemToFormValues(item)}
        submitLabel="Save changes"
        onSubmit={handleSubmit}
        submitting={saving}
        onSuccess={handleSuccess}
        onCancel={() => navigate(ROUTES.CATALOG)}
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Edit product"
        description="Update the details of this product."
        actions={
          <Button variant="secondary" onClick={() => navigate(ROUTES.CATALOG)}>
            Back to catalog
          </Button>
        }
      />
      {body}
    </div>
  );
}

export default EditProductPage;
